import React, { useEffect, useState } from "react";
import { Spin, Tooltip, message, Pagination } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { Wallet, CopyIcon, Plug, Star } from "lucide-react";
import { motion } from "framer-motion";
import userApi from "../../api/usersApi";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const short = (addr) => (addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "—");

const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Скопировано");
};

export default function WalletsPage() {
    const [walletsData, setWalletsData] = useState(null);
    const [walletsLoading, setWalletsLoading] = useState(false);
    const [walletsPage, setWalletsPage] = useState(1);
    const [walletsLimit] = useState(50);

    const fetchConnectedWallets = async (p = walletsPage) => {
        setWalletsLoading(true);
        try {
            const res = await userApi.getAllConnectedWallets(p, walletsLimit);
            setWalletsData(res);
        } catch (e) {
            console.error(e);
            message.error("Не удалось загрузить подключенные кошельки");
        } finally {
            setWalletsLoading(false);
        }
    };

    useEffect(() => {
        fetchConnectedWallets(walletsPage);
    }, [walletsPage]);

    const walletsItems = walletsData?.items || [];
    const walletsTotal = walletsData?.total || 0;

    const th = "whitespace-nowrap px-3 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wide text-white/50 sm:px-4";
    const td = "px-3 py-3 text-[12px] text-white/85 sm:px-4";

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">

            {/* Header */}
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <Wallet className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                        </span>
                        <div>
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                                Подключённые кошельки
                            </h1>
                            <p className="text-[12px] text-white/45">
                                Список пользователей и их подключённых on-chain кошельков
                            </p>
                        </div>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => fetchConnectedWallets(walletsPage)}
                        disabled={walletsLoading}
                        className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-2 text-[13px] text-white/85 hover:border-white/20 hover:bg-white/10 disabled:opacity-50"
                    >
                        <ReloadOutlined spin={walletsLoading} />
                        Обновить
                    </motion.button>
                </div>
            </section>

            {/* Wallets table */}
            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col overflow-hidden")}>
                <div className="flex items-center gap-2 border-b border-white/[0.06] px-4 py-3 sm:px-5">
                    <Plug size={15} className="text-emerald-300/80" />
                    <h2 className="text-[13px] font-semibold uppercase tracking-wide text-white/75">
                        Кошельки
                    </h2>
                    <span className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-200">
                        {walletsTotal}
                    </span>
                </div>

                <div className="relative min-h-0 flex-1">
                    {walletsLoading && !walletsItems.length ? (
                        <div className="flex min-h-[280px] flex-1 items-center justify-center">
                            <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                        </div>
                    ) : (
                        <>
                            {walletsLoading && walletsItems.length > 0 && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#070A12]/45 backdrop-blur-sm">
                                    <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                                </div>
                            )}
                            <div className="h-full min-h-[320px] overflow-auto">
                                <table className="w-full min-w-[800px] border-collapse text-left">
                                    <thead className="sticky top-0 z-[1] bg-[#0a0e18]/90 text-white/70 backdrop-blur-md">
                                        <tr className="border-b border-white/10">
                                            <th className={th}>Пользователь</th>
                                            <th className={th}>Telegram</th>
                                            <th className={th}>Кошелёк</th>
                                            <th className={th}>Chain</th>
                                            <th className={th}>Подключён</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {walletsItems.map((w) => (
                                            <tr key={w.id} className="border-t border-white/[0.06] hover:bg-white/[0.04]">
                                                <td className={td}>
                                                    <div className="flex items-center gap-2">
                                                        {w.isPrimary && (
                                                            <Tooltip title="Primary кошелёк">
                                                                <Star size={12} className="shrink-0 text-amber-300/80" fill="currentColor" />
                                                            </Tooltip>
                                                        )}
                                                        <span className="text-white/85">
                                                            {[w.user?.firstName, w.user?.lastName].filter(Boolean).join(" ") || "—"}
                                                        </span>
                                                        <span className="text-[11px] text-white/35">#{w.user?.id ?? "—"}</span>
                                                    </div>
                                                </td>
                                                <td className={cn(td, "font-mono text-[11px] text-white/55")}>
                                                    {w.user?.telegramId || "—"}
                                                </td>
                                                <td className={td}>
                                                    <Tooltip title={w.walletAddress}>
                                                        <button
                                                            type="button"
                                                            onClick={() => copyToClipboard(w.walletAddress)}
                                                            className="flex items-center gap-1 font-mono text-[12px] text-white/75 hover:text-white"
                                                        >
                                                            <Wallet size={11} className="shrink-0 text-emerald-400/70" />
                                                            {short(w.walletAddress)}
                                                            <CopyIcon size={10} className="text-white/30" />
                                                        </button>
                                                    </Tooltip>
                                                    {w.label && (
                                                        <span className="ml-2 rounded border border-white/10 bg-white/[0.05] px-1.5 py-0.5 text-[10px] text-white/55">
                                                            {w.label}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className={cn(td, "font-mono text-[11px] text-white/55")}>
                                                    {w.walletChainId || "—"}
                                                </td>
                                                <td className={cn(td, "text-[11px] text-white/45")}>
                                                    {w.connectedAt
                                                        ? new Date(w.connectedAt).toLocaleString("ru-RU", {
                                                              day: "2-digit", month: "2-digit", year: "2-digit",
                                                              hour: "2-digit", minute: "2-digit",
                                                          })
                                                        : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {walletsItems.length === 0 && !walletsLoading && (
                                    <div className="flex flex-col items-center py-16 text-white/40">
                                        <Plug className="mb-3 h-12 w-12 opacity-30" />
                                        <p className="text-[14px]">Никто ещё не подключал кошелёк</p>
                                        <p className="mt-1 text-[12px] text-white/30">
                                            Записи появятся после первого подключения через MetaMask/WalletConnect
                                        </p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {walletsTotal > walletsLimit && (
                    <div className="border-t border-white/[0.06] px-4 py-3">
                        <Pagination
                            current={walletsPage}
                            pageSize={walletsLimit}
                            total={walletsTotal}
                            onChange={(p) => setWalletsPage(p)}
                            className="pagination-custom !mb-0 flex justify-end"
                            size="small"
                        />
                    </div>
                )}
            </section>
        </div>
    );
}
